import React, { useEffect, useMemo, useRef, useState } from 'react';
import FamilyDialog from './FamilyDialog';
import type { FamilyToolsMember } from './types';
import { callManageFamily } from './manageFamilyClient';
import { isJuniorForYear, clubYear } from '@shared/membership';

interface RemoveFromFamilyDialogProps {
  member: FamilyToolsMember;
  /** Every other active member currently in the same family. */
  familyMembers: FamilyToolsMember[];
  onClose: () => void;
  onDone: (result: { message: string; changedMemberIds: string[] }) => void;
}

/**
 * Confirms removing `member` from their family (R10, REMOVE). Removing the
 * primary requires choosing a new primary from the remaining adults first.
 */
const RemoveFromFamilyDialog: React.FC<RemoveFromFamilyDialogProps> = ({
  member,
  familyMembers,
  onClose,
  onDone,
}) => {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const year = clubYear();
  const remainingAdults = useMemo(
    () =>
      familyMembers.filter(
        (m) => m.id !== member.id && !isJuniorForYear(m.dateOfBirth, year)
      ),
    [familyMembers, member.id, year]
  );

  const [newPrimaryMemberId, setNewPrimaryMemberId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  const removingPrimary = !!member.familyPrimary;
  // The server dissolves a family left with fewer than two people (R10.5), so
  // a new primary is only chosen when two or more people would remain.
  const remainingCount = familyMembers.filter((m) => m.id !== member.id).length;
  const willDissolve = remainingCount < 2;
  const needsNewPrimary = removingPrimary && !willDissolve && remainingAdults.length > 0;
  const noAdultLeft = removingPrimary && !willDissolve && remainingAdults.length === 0;
  const canConfirm = !noAdultLeft && (!needsNewPrimary || !!newPrimaryMemberId);

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    setServerError(null);
    try {
      const payload: Record<string, unknown> = { memberId: member.id };
      if (newPrimaryMemberId) payload.newPrimaryMemberId = newPrimaryMemberId;
      const result = await callManageFamily('REMOVE', payload);
      if (result.success) {
        onDone({
          message: result.message || `${member.firstName} ${member.lastName} is now an individual member.`,
          changedMemberIds: result.changedMemberIds || [],
        });
      } else {
        setServerError(result.message || 'Could not remove this member from the family. Please try again.');
      }
    } catch (err: any) {
      setServerError(err?.message || 'Could not remove this member from the family. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FamilyDialog
      role="alertdialog"
      titleId="remove-from-family-title"
      title="Remove from family?"
      descriptionId="remove-from-family-desc"
      busy={submitting}
      onClose={onClose}
    >
      <div id="remove-from-family-desc" className="mt-2 space-y-2 text-sm text-gray-700">
        <p>
          Remove <strong>{member.firstName} {member.lastName}</strong> from the family?
        </p>
        <p className="rounded-md border border-amber-300 bg-amber-50 p-2 font-medium text-amber-900">
          They keep their membership as an individual for the rest of the year. Nothing is
          refunded.
        </p>
      </div>

      {willDissolve ? (
        <p data-testid="rff-dissolve-note" className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-2 text-sm text-gray-700">
          This leaves only one person, so the family ends and they also become an individual member.
        </p>
      ) : needsNewPrimary ? (
        <div className="mt-3">
          <label htmlFor="rff-new-primary" className="block text-sm font-medium text-gray-700 mb-1">
            Choose the new primary *
          </label>
          <select
            id="rff-new-primary"
            value={newPrimaryMemberId || ''}
            onChange={(e) => setNewPrimaryMemberId(e.target.value || null)}
            className="min-h-[44px] w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-fishing-blue"
          >
            <option value="">Choose the new primary</option>
            {remainingAdults.map((m) => (
              <option key={m.id} value={m.id}>
                {m.firstName} {m.lastName}
              </option>
            ))}
          </select>
        </div>
      ) : noAdultLeft ? (
        <p role="alert" className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900">
          No other adult would remain to be the primary. Add another adult to the family first, or contact the members about what should happen.
        </p>
      ) : null}

      {serverError && (
        <p role="alert" className="mt-3 text-sm font-medium text-red-700">
          {serverError}
        </p>
      )}

      <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button
          ref={cancelRef}
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="min-h-[44px] rounded-lg border border-gray-300 px-4 py-2 font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
        >
          Keep in family
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={submitting || !canConfirm}
          className="min-h-[44px] rounded-lg bg-red-700 px-4 py-2 font-semibold text-white hover:bg-red-800 disabled:opacity-50"
        >
          {submitting ? 'Removing…' : 'Remove from family'}
        </button>
      </div>
    </FamilyDialog>
  );
};

export default RemoveFromFamilyDialog;
